import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useId,
	useRef,
	useState,
	type ComponentProps,
	type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { Button } from './button';
import { useHideTabBar } from './tab-bar';

/** The visible viewport (it shrinks when the keyboard opens on iOS); null where unsupported. */
function useVisibleViewport(): { height: number; top: number } | null {
	const [box, setBox] = useState<{ height: number; top: number } | null>(null);
	useEffect(() => {
		// Typed non-optional, but missing in older WebViews and jsdom.
		const vv = window.visualViewport as VisualViewport | null | undefined;
		if (vv === null || vv === undefined) return;
		const update = () => {
			setBox({ height: vv.height, top: vv.offsetTop });
		};
		update();
		vv.addEventListener('resize', update);
		vv.addEventListener('scroll', update);
		return () => {
			vv.removeEventListener('resize', update);
			vv.removeEventListener('scroll', update);
		};
	}, []);
	return box;
}

/** The sheet's animated close, for buttons inside it (T-1311). */
const SheetClose = createContext<(() => void) | null>(null);

/** A button that closes the sheet it sits in with the slide-down («Скасувати», «Пізніше»). */
export function SheetCloseButton(props: Omit<ComponentProps<typeof Button>, 'onClick'>) {
	const close = useContext(SheetClose);
	return <Button {...props} onClick={close ?? undefined} />;
}

/** The close animation's length (DESIGN → Motion, `base`); the sheet unmounts after it. */
export const SHEET_EXIT_MS = 200;

/**
 * Bottom sheet with a drag handle (DESIGN → Components). Closes on backdrop tap or Escape.
 * Rendered into `body` so no transformed or scrolling ancestor can clip it or put the tab bar on
 * top; `actions` stay pinned above the safe area while the body scrolls (T-1302).
 */
export function Sheet({
	title,
	onClose,
	children,
	actions,
}: {
	title: string;
	onClose: () => void;
	children: ReactNode;
	actions?: ReactNode;
}) {
	const titleId = useId();
	const viewport = useVisibleViewport();
	// Backdrop and Escape slide the sheet down first, then close it (T-1310).
	const [closing, setClosing] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const close = useCallback(() => {
		if (timer.current !== null) return;
		setClosing(true);
		timer.current = setTimeout(onClose, SHEET_EXIT_MS);
	}, [onClose]);
	useEffect(
		() => () => {
			if (timer.current !== null) clearTimeout(timer.current);
		},
		[],
	);
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') close();
		};
		window.addEventListener('keydown', onKey);
		return () => {
			window.removeEventListener('keydown', onKey);
		};
	}, [close]);
	// The actions sit where the tab bar is (T-1302).
	useHideTabBar();

	return createPortal(
		<SheetClose.Provider value={close}>
			<div
				className="fixed inset-x-0 top-0 z-50 flex h-full flex-col justify-end"
				style={viewport === null ? undefined : { height: viewport.height, top: viewport.top }}
			>
				<button
					type="button"
					aria-hidden="true"
					tabIndex={-1}
					className={`bg-bg/70 absolute inset-0 ${closing ? 'animate-fade-out' : 'animate-fade-in'}`}
					onClick={close}
				/>
				<section
					role="dialog"
					aria-modal="true"
					aria-labelledby={titleId}
					className={`bg-surface rounded-t-sheet pb-sheet-bottom relative flex max-h-full flex-col px-4 pt-2 ${closing ? 'animate-sheet-down' : 'animate-sheet-up'}`}
				>
					<div
						aria-hidden="true"
						className="bg-border mx-auto mb-4 h-1 w-10 shrink-0 rounded-full"
					/>
					<h2 id={titleId} className="text-h2 text-text mb-3 shrink-0 font-bold">
						{title}
					</h2>
					<div className="min-h-0 overflow-y-auto pb-4">{children}</div>
					{actions !== undefined && (
						<div className="flex shrink-0 flex-col gap-3 pb-4">{actions}</div>
					)}
				</section>
			</div>
		</SheetClose.Provider>,
		document.body,
	);
}
